import {
  MICE_DELETABLE_STATUSES,
  MICE_PROFILE_FIELDS,
  type MiceNeedStatus,
  type MiceNeedView,
  type MiceOrderStatus,
  type MiceOrderView,
  type MiceProfileCheckView,
  type MiceTraveler,
  profileBlocked,
  profileNeedsAction,
} from '@voll-intelligence/domain';

export type MiceBulkAction =
  | 'recommend'
  | 'approve'
  | 'manual'
  | 'buy'
  | 'retry'
  | 'unqueue'
  | 'remove'
  | 'profile_sheet'
  | 'profile_keep';

/** Ações do cadastro em lote: recebem `MiceTraveler.id` (necessidade ou `order:<id>`). */
type ProfileAction = Extract<MiceBulkAction, 'profile_sheet' | 'profile_keep'>;

const isProfileAction = (action: MiceBulkAction): action is ProfileAction =>
  action === 'profile_sheet' || action === 'profile_keep';

/** A checagem que o servidor decide: a da necessidade; sem ela, a do pedido de planilha. */
export const travelerProfileCheck = (t: MiceTraveler): MiceProfileCheckView | null =>
  t.need ? t.need.profileCheck : (t.orders[0]?.order.profileCheck ?? null);

/**
 * "Gravar dados da planilha" só leva campo `to_complete` (divergência se decide viajante por
 * viajante); "Manter cadastro da VOLL" leva qualquer campo à espera de decisão.
 */
function profileEligible(action: ProfileAction, t: MiceTraveler): boolean {
  const check = travelerProfileCheck(t);
  if (!check) return false;
  return MICE_PROFILE_FIELDS.some((field) => {
    const { state } = check.fields[field];
    return state === 'to_complete' || (action === 'profile_keep' && state === 'divergent');
  });
}

/** Teto de `MiceRecommendRequestSchema`: a recomendação sai em lotes deste tamanho. */
export const RECOMMEND_BATCH = 10;

/** O cadastro em lote sai em pedidos deste tamanho (o servidor resolve um viajante por vez). */
export const PROFILE_BULK_BATCH = 25;

/** `ids` em lotes de até `size`, na ordem. */
export const chunks = (ids: readonly string[], size: number): string[][] =>
  Array.from({ length: Math.ceil(ids.length / size) }, (_, i) =>
    ids.slice(i * size, (i + 1) * size),
  );

/** Status da necessidade em que cada ação se aplica (os mesmos filtros do servidor). */
const NEED_ELIGIBLE = {
  recommend: ['ready', 'recommended', 'no_solution'],
  approve: ['recommended'],
  manual: [
    'validating',
    'invalid',
    'duplicate',
    'ready',
    'recommending',
    'recommended',
    'no_solution',
    'reconsidering',
    'approved',
    // Só sai de `ordered` sem pedido vivo: o servidor decide e não conta a que ficou.
    'ordered',
  ],
  buy: ['approved'],
} as const satisfies Record<string, readonly MiceNeedStatus[]>;

/** Status do pedido em que cada ação se aplica; `retry` põe na fila (primeira compra ou de novo). */
const ORDER_ELIGIBLE = {
  retry: ['ready', 'failed'],
  unqueue: ['queued'],
  remove: MICE_DELETABLE_STATUSES,
} as const satisfies Record<string, readonly MiceOrderStatus[]>;

type NeedAction = keyof typeof NEED_ELIGIBLE;

const isNeedAction = (action: MiceBulkAction): action is NeedAction => action in NEED_ELIGIBLE;

/** O status da ação, e comprar leva também a recomendada verde (o servidor a aprova no passo). */
function needEligible(action: NeedAction, need: MiceNeedView): boolean {
  const statuses: readonly MiceNeedStatus[] = NEED_ELIGIBLE[action];
  if (statuses.includes(need.status)) return true;
  return (
    action === 'buy' && need.status === 'recommended' && need.recommendation?.color === 'green'
  );
}

/**
 * Quem o cadastro trava na ação. Recomendar também deixa de fora o aviso antigo (sem checagem): é o
 * `profileBlocked` do domínio, como no servidor. Comprar e repetir só pela checagem: o aviso antigo
 * continua na revisão da compra, que o destaca.
 */
const profileBlockedFor = (action: MiceBulkAction, t: MiceTraveler, order?: MiceOrderView) => {
  if (action === 'recommend')
    return t.need !== null && profileBlocked(t.need.profileCheck, t.need.error);
  if (action === 'buy') return profileNeedsAction(t.need?.profileCheck ?? null);
  if (action === 'retry') {
    if (t.need) return profileNeedsAction(t.need.profileCheck);
    return order?.status === 'ready' && profileNeedsAction(order.profileCheck);
  }
  return false;
};

/**
 * Ids que a ação recebe: `needIds` (recommend, approve, manual, buy), ids de viajante (cadastro) ou
 * `orderIds` (o resto).
 */
export function eligibleIds(action: MiceBulkAction, selected: readonly MiceTraveler[]): string[] {
  if (isProfileAction(action)) {
    return selected.flatMap((t) => (profileEligible(action, t) ? [t.id] : []));
  }
  if (isNeedAction(action)) {
    return selected.flatMap((t) =>
      t.need && needEligible(action, t.need) && !profileBlockedFor(action, t) ? [t.need.id] : [],
    );
  }
  const statuses: readonly MiceOrderStatus[] = ORDER_ELIGIBLE[action];
  return selected.flatMap((t) =>
    t.orders.flatMap(({ order }) =>
      statuses.includes(order.status) && !profileBlockedFor(action, t, order) ? [order.id] : [],
    ),
  );
}

/** Quantos selecionados não entram em nenhuma das ações oferecidas. */
export function outOfActions(
  selected: readonly MiceTraveler[],
  actions: readonly MiceBulkAction[],
): number {
  return selected.filter((t) => actions.every((a) => eligibleIds(a, [t]).length === 0)).length;
}
